// --- Domain model types ---

export type SwimlaneConfig = {label: string; color: string; minX: number; maxX: number};

type EntityLevelControlSection = {title: string; description: string; diagramId?: string};

export type EntityLevelControl = {
  id: string;
  name: string;
  description: string;
  diagramId?: string;
  sections?: EntityLevelControlSection[];
};

export type Assertion = {id: string; name: string; description: string};

export type Process = {id: string; name: string; description: string; fsaIds: string[]; diagramId?: string};

export type FinancialStatementArea = {id: string; name: string; description: string};

export type COTABD = {
  id: string;
  name: string;
  type: "transaction" | "balance" | "disclosure";
  fsaId: string;
  assertionIds: string[];
  processIds: string[];
  material: boolean;
  significant: boolean;
};

export type MockSourceEvidence = {
  source_id: string;
  source_name: string;
  source_type: "document" | "image" | "questionnaire";
  source_context: string;
  relevance_explanation: string;
  extractions: Array<{citation_number: number; quote: string; page_reference?: string}>;
};

export type MockAIGeneration = {
  generation_id: string;
  generated_at: string;
  model_version: string;
  prompt: string;
  diagram_type: string;
  source_evidence: MockSourceEvidence[];
  reasoning: string;
  confidence_notes: string[];
};

export type RiskRating = "significant" | "medium" | "low";

export type Isa315ComponentName = "complexity" | "subjectivity" | "change" | "uncertainty" | "fraudSusceptibility";

type RiskScoreComponents = Record<Isa315ComponentName, number>; // each 1–5

export type RiskScore = {components: RiskScoreComponents; rationale: string; assessedBy: string; assessedAt: Date};

export type EvaluationScore = 1 | 2 | 3 | null;

export type Control = {
  id: string;
  controlNumber: string;
  title: string;
  description: string;
  designScore: EvaluationScore;
  implementationScore: EvaluationScore;
  lastAssessed: Date | null;
  status: ProcessStatus;
};

export type Risk = {
  id: string;
  riskNumber: string;
  title: string;
  description: string;
  owner: string | null;
  inherentScore: RiskScore | null;
  residualLevel: RiskRating | null;
  residualScore: RiskScore | null;
  status: ProcessStatus;
  cotabdAssertions: {cotabdId: string; assertionId: string}[];
  controls: Control[];
};

export type ProcessRisksAndControls = {processId: string; risks: Risk[]};

export type ElcRisksAndControls = {elcId: string; risks: Risk[]};

export type FindingRating = "low" | "moderate" | "high";

type FindingSection = {title: string; content: string};

export type Finding = {
  id: string;
  rating: FindingRating;
  internal: FindingSection;
  clientFacing: FindingSection | null;
  controlId: string | null;
  includeInternal: boolean;
  includeClientFacing: boolean;
  status: ProcessStatus;
};

export type ProcessFindingsData = {processId: string; findings: Finding[]};
export type ElcFindingsData = {elcId: string; findings: Finding[]};

type MockComplexityValueType = "Integer" | "Boolean" | "StringSet";

type MockComplexityCondition = {label: string; evaluate: (value: number | boolean | string[]) => boolean};

export type MockComplexityRule = {conditions: MockComplexityCondition[]; score: number};

export type MockComplexityAttribute = {
  id: string;
  name: string;
  valueType: MockComplexityValueType;
  weight: number;
  rules: MockComplexityRule[];
  defaultScore: number | null;
  operator: "First" | "Max" | "Sum";
  initialValue: number | boolean | string[];
  options?: string[];
  sourceCount: number;
};

export type MockComplexityTier = {
  name: string;
  colorPalette: string;
  lowerBound: number | null;
  upperBound: number | null;
};

export type MockComplexityConfig = {tiers: MockComplexityTier[]; attributes: MockComplexityAttribute[]};

type ProcessAttributeKind = "Text" | "Number" | "SingleSelect";

export type ProcessAttributeTemplate = {
  key: string;
  title: string;
  kind: ProcessAttributeKind;
  options?: string[];
  placeholder?: string;
};

export type MockEvidenceSourceType = "Document" | "Image" | "Video" | "Questionnaire";

export type MockEvidence = {
  id: string;
  citationId: number;
  name: string;
  sourceType: MockEvidenceSourceType;
  scope: string;
  context: string;
};

export type MockActivity = {id: string; user: string; description: string; body?: string; timestamp: Date};

export type MockChatMessage = {id: string; user: string; message: string; timestamp: Date};
export type MockPageCollaboration = {messages: MockChatMessage[]; unreadCount: number};

// --- UI types ---

type MockQuestionType = "text" | "yes-no" | "select" | "multi-select";

export type MockQuestion = {
  id: string;
  text: string;
  type: MockQuestionType;
  options?: string[];
  answer: string | string[] | null;
  note?: string;
};

type MockQuestionnaireStatus = "draft" | "in-progress" | "submitted" | "locked";
type MockInternalQuestionnaireStatus = "not-started" | "in-progress" | "completed";

type MockExternalQuestionnaire = {
  id: string;
  citationId: number;
  templateName: string;
  status: MockQuestionnaireStatus;
  createdAt: Date;
  submittedAt: Date | null;
  lockedAt: Date | null;
  responseCount: number;
  totalQuestions: number;
  questions: MockQuestion[];
};

type MockInternalQuestionnaire = {
  id: string;
  citationId: number;
  title: string;
  status: MockInternalQuestionnaireStatus;
  assignee: string;
  completedCount: number;
  totalQuestions: number;
  createdAt: Date;
  completedAt: Date | null;
  questions: MockQuestion[];
};

export type MockQuestionnaire =
  | {type: "external"; questionnaire: MockExternalQuestionnaire}
  | {type: "internal"; questionnaire: MockInternalQuestionnaire};

export type ElcSubPage =
  | "understanding"
  | "complexity"
  | "questionnaires"
  | "risks"
  | "controls"
  | "findings"
  | "matrix"
  | "summary";

export type FsaSubPage = "scoping" | "risk-assessment" | "linked-processes" | "strategy";

/** What the procedure is trying to prove — maps 1:1 to an execution module */
export type TestingMethodology =
  | "test-of-controls" // → OE module (built)
  | "substantive-analytical" // → future analytical procedures module
  | "substantive-detail"; // → future detail testing module

/** How evidence is gathered — a procedure typically uses multiple */
export type EvidenceTechnique =
  | "inquiry" // interviews, walkthroughs
  | "observation" // watching process execution
  | "inspection" // reviewing documents/records
  | "reperformance" // independently re-executing a control or calculation
  | "confirmation"; // third-party confirmation (e.g. AR confirmations)
type AuditProcedureTiming = "interim" | "year-end" | "both";

export type AuditProcedure = {
  id: string;
  procedureNumber: string;
  title: string;
  description: string;
  methodology: TestingMethodology;
  techniques: EvidenceTechnique[];
  timing: AuditProcedureTiming;
  extent: string;
  assignedTo: string | null;
  status: ProcessStatus;
  fsaId: string | null;
  cotabdId: string | null;
  assertionIds: string[];
  riskIds: string[];
  controlIds: string[];
  processId: string | null;
  elcId: string | null;
};

export type AuditProgrammeSubPage = "by-fsa" | "by-process" | "by-elc";

export type FraudSubPage = "risk-factors" | "team-discussion" | "identified-risks" | "management-override" | "strategy";

export const FRAUD_SUB_PAGES: SubPageDef<FraudSubPage>[] = [
  {key: "risk-factors", label: "Risk Factors", showDot: true},
  {key: "team-discussion", label: "Team Discussion", showDot: true},
  {key: "identified-risks", label: "Identified Risks", showDot: true},
  {key: "management-override", label: "Management Override", showDot: true},
  {key: "strategy", label: "Audit Strategy", showDot: true},
];

export type FraudRiskFactor = {
  id: string;
  description: string;
  riskLevel: "High" | "Medium" | "Low";
  sourceTags: string[];
};

export type FraudTeamDiscussion = {
  date: string;
  participants: string[];
  location: string;
  videoMeta: {duration: string; uploadedAt: string} | null;
  transcript: {speaker: string; timestamp: string; text: string}[];
  agendaTopics: {title: string; summary: string; citations: MockEvidence[]}[];
};

export type ManagementOverrideArea = {id: string; title: string; description: string; procedureIds: string[]};

export type Selection =
  | {type: "tool"; toolId: string}
  | {type: "elc"; elcId: string; subPage?: ElcSubPage}
  | {type: "fsa"; fsaId: string; subPage: FsaSubPage}
  | {
      type: "process";
      processId: string;
      subPage: "understanding" | "risks" | "controls" | "findings" | "matrix" | "questionnaires" | "summary";
    }
  | {type: "materiality"}
  | {type: "fraud"; subPage?: FraudSubPage}
  | {type: "risk-register"}
  | {type: "audit-programme"; subPage: AuditProgrammeSubPage};

export type ProcessStatus = "not-ready" | "in-progress" | "in-review" | "complete";

export type PageLockState = {locked: boolean; lockedBy?: string; lockedAt?: Date};
export type MockReviewStatus = "pending" | "approved" | "changes-requested" | "commented";
export type SignOffEntry = {signer: string; status: MockReviewStatus; comment?: string} | null;
export type ConfiguredRole = "preparer" | "reviewer" | "manager";
export type AdditionalReviewer = {id: string; signer: string; status: MockReviewStatus; comment?: string};
export type SignOffs = {
  preparer: SignOffEntry;
  reviewer: SignOffEntry;
  manager: SignOffEntry;
  additional: AdditionalReviewer[];
};

export type ReportEntityType =
  | "elc"
  | "process"
  | "fsa"
  | "fraud"
  | "materiality"
  | "audit-programme"
  | "audit-planning";

export type ReportOption = {key: string; label: string; type: ReportEntityType; entityId?: string; enabled: boolean};

export function getAvailableReports(
  selection: Selection,
  resolveEntityName?: (type: "elc" | "process" | "fsa", id: string) => string,
): ReportOption[] {
  const reports: ReportOption[] = [];
  const resolveName = resolveEntityName ?? ((_t, id) => id);

  if (selection.type === "elc") {
    const elcName = resolveName("elc", selection.elcId);
    if (selection.subPage) {
      const subPageLabel = ELC_SUB_PAGE_LABELS[selection.subPage];
      reports.push({
        key: `elc-subpage:${selection.elcId}:${selection.subPage}`,
        label: `${elcName} › ${subPageLabel}`,
        type: "elc",
        entityId: selection.elcId,
        enabled: false,
      });
    }
    reports.push({
      key: `elc:${selection.elcId}`,
      label: elcName,
      type: "elc",
      entityId: selection.elcId,
      enabled: true,
    });
    reports.push({key: "all-elcs", label: "All ELCs", type: "elc", enabled: false});
  }

  if (selection.type === "process") {
    const processName = resolveName("process", selection.processId);
    const subPageLabel = PROCESS_SUB_PAGES.find(s => s.key === selection.subPage)?.label;
    if (subPageLabel) {
      reports.push({
        key: `process-subpage:${selection.processId}:${selection.subPage}`,
        label: `${processName} › ${subPageLabel}`,
        type: "process",
        entityId: selection.processId,
        enabled: false,
      });
    }
    reports.push({
      key: `process:${selection.processId}`,
      label: processName,
      type: "process",
      entityId: selection.processId,
      enabled: true,
    });
  }

  if (selection.type === "fsa") {
    const fsaName = resolveName("fsa", selection.fsaId);
    reports.push({
      key: `fsa:${selection.fsaId}`,
      label: fsaName,
      type: "fsa",
      entityId: selection.fsaId,
      enabled: false,
    });
  }

  if (selection.type === "fraud") {
    reports.push({key: "fraud", label: "Fraud & Management Override", type: "fraud", enabled: false});
  }

  if (selection.type === "materiality") {
    reports.push({key: "materiality", label: "Materiality", type: "materiality", enabled: false});
  }

  if (selection.type === "audit-programme") {
    reports.push({key: "audit-programme", label: "Audit Programme", type: "audit-programme", enabled: false});
  }

  reports.push({key: "audit-planning", label: "Audit Planning", type: "audit-planning", enabled: false});
  return reports;
}

export type CollaborationProps = {
  status: ProcessStatus;
  onStatusChange: (status: ProcessStatus) => void;
  locked: boolean;
  lockedBy?: string;
  lockedAt?: Date;
  onLockChange: (locked: boolean) => void;
  signOffs: SignOffs;
  messages: MockChatMessage[];
  unreadCount: number;
  onOpenCollaboration: () => void;
  selection: Selection;
  onOpenReport: (report: ReportOption) => void;
};

export type ProcessHeaderProps = {process: Process} & CollaborationProps;

export type ProcessFsaProps = {
  onFsaClick: (fsaId: string) => void;
  fsaIds: string[];
  onFsaChange: (fsaIds: string[]) => void;
};

export type ElcHeaderProps = {elc: EntityLevelControl} & CollaborationProps;

export type DiscoveryStatus = "pending" | "accepted" | "rejected";

export type ProcessSuggestion = {
  id: string;
  name: string;
  description: string;
  fsaIds: string[];
  sourceEvidence: Array<{documentName: string; quote: string; pageRef?: string}>;
};

export type DiscoveryMatchedProcess = {
  processId: string;
  confirmationNote: string;
  sourceEvidence: Array<{documentName: string; quote: string; pageRef?: string}>;
};

export type DiscoverySourceDocument = {id: string; name: string; type: string; citationCount: number};

export type EntityRiskRegisterEntry = {
  id: string;
  riskId: string;
  description: string;
  category: string;
  categoryColor: string;
  financialReportingImpact: string;
  linkedFsaIds: string[];
  entityResponse: string;
  status: "open" | "mitigated" | "accepted";
};

export type MaturityDimension = {id: string; name: string; score: EvaluationScore; rationale: string};

export type ChangeAssessmentEntry = {
  id: string;
  change: string;
  identified: boolean;
  assessed: boolean;
  responded: boolean;
  notes: string;
};

export type EditMeta = {editedBy: "ai" | "human"; editedAt: Date};

export type Attestation = {attestedBy: string; attestedAt: Date};

// --- Constants ---

export function questionnaireToProcessStatus(q: MockQuestionnaire): ProcessStatus {
  if (q.type === "external") {
    const s = q.questionnaire.status;
    if (s === "locked" || s === "submitted") return "complete";
    if (s === "in-progress") return "in-progress";
    return "not-ready";
  }
  const s = q.questionnaire.status;
  if (s === "completed") return "complete";
  if (s === "in-progress") return "in-progress";
  return "not-ready";
}

const ELCS_WITH_SUBPAGES = new Set([
  "elc-it-environment",
  "elc-monitoring-governance",
  "elc-control-environment",
  "elc-risk-assessment",
]);
export function elcHasSubPages(elcId: string): boolean {
  return ELCS_WITH_SUBPAGES.has(elcId);
}

export const STATUS_LABELS: Record<ProcessStatus, string> = {
  "not-ready": "Not Ready",
  "in-progress": "In Progress",
  "in-review": "In Review",
  complete: "Complete",
};

export const DEFAULT_SIGN_OFFS: SignOffs = {preparer: null, reviewer: null, manager: null, additional: []};

export const FSA_SUB_PAGES: {key: FsaSubPage; label: string}[] = [
  {key: "scoping", label: "Scoping"},
  {key: "risk-assessment", label: "Risk Assessment"},
  {key: "linked-processes", label: "Linked Processes"},
  {key: "strategy", label: "Audit Strategy"},
];

type SubPageGroup = {label: string; statusKey: string};

export type SubPageDef<K extends string> = {key: K; label: string; group?: SubPageGroup; showDot?: boolean};

const RISKS_CONTROLS_GROUP: SubPageGroup = {label: "Risks & Controls", statusKey: "risks-controls"};

type ProcessSubPage = "understanding" | "questionnaires" | "matrix" | "risks" | "controls" | "findings" | "summary";

export const PROCESS_SUB_PAGES: SubPageDef<ProcessSubPage>[] = [
  {key: "questionnaires", label: "Questionnaires & Relevant Evidence", showDot: true},
  {key: "understanding", label: "Understanding", showDot: true},
  {key: "matrix", label: "Risk–Control Matrix", group: RISKS_CONTROLS_GROUP},
  {key: "risks", label: "Risks", group: RISKS_CONTROLS_GROUP},
  {key: "controls", label: "Controls", group: RISKS_CONTROLS_GROUP},
  {key: "findings", label: "Findings", group: RISKS_CONTROLS_GROUP},
  {key: "summary", label: "Audit Strategy", showDot: true},
];

const ELC_SUB_PAGES: SubPageDef<ElcSubPage>[] = [
  {key: "questionnaires", label: "Questionnaires & Relevant Evidence", showDot: true},
  {key: "understanding", label: "Understanding", showDot: true},
  {key: "complexity", label: "Complexity", showDot: true},
  {key: "matrix", label: "Risk–Control Matrix", group: RISKS_CONTROLS_GROUP},
  {key: "risks", label: "Risks", group: RISKS_CONTROLS_GROUP},
  {key: "controls", label: "Controls", group: RISKS_CONTROLS_GROUP},
  {key: "findings", label: "Findings", group: RISKS_CONTROLS_GROUP},
  {key: "summary", label: "Audit Strategy", showDot: true},
];

export const AUDIT_PROGRAMME_SUB_PAGES: SubPageDef<AuditProgrammeSubPage>[] = [
  {key: "by-fsa", label: "By Financial Statement Area", showDot: true},
  {key: "by-process", label: "By Process", showDot: true},
  {key: "by-elc", label: "By Entity-Level Control", showDot: true},
];

export function statusKeyForSubPage<K extends string>(
  prefix: string,
  subPageKey: K,
  subPages: SubPageDef<K>[],
): string {
  const def = subPages.find(s => s.key === subPageKey);
  if (def?.group) return `${prefix}:${def.group.statusKey}`;
  return `${prefix}:${subPageKey}`;
}

export const ELC_SUB_PAGE_LABELS: Record<ElcSubPage, string> = {
  understanding: "Understanding",
  complexity: "Complexity",
  questionnaires: "Questionnaires & Relevant Evidence",
  risks: "Risks",
  controls: "Controls",
  findings: "Findings",
  matrix: "Risk–Control Matrix",
  summary: "Audit Strategy",
};

const ELC_SUB_PAGE_EXCLUSIONS: Record<string, Set<ElcSubPage>> = {
  "elc-monitoring-governance": new Set(["complexity"]),
  "elc-control-environment": new Set(["complexity"]),
  "elc-risk-assessment": new Set(["complexity"]),
};

export function getElcSubPages(elcId: string): SubPageDef<ElcSubPage>[] {
  const exclusions = ELC_SUB_PAGE_EXCLUSIONS[elcId];
  if (!exclusions) return ELC_SUB_PAGES;
  return ELC_SUB_PAGES.filter(s => !exclusions.has(s.key));
}

export function getElcSubPageLabel(_elcId: string, subPage: ElcSubPage): string {
  return ELC_SUB_PAGE_LABELS[subPage];
}

export const COTABD_TYPE_COLORS: Record<COTABD["type"], string> = {
  transaction: "blue",
  balance: "green",
  disclosure: "purple",
};

// --- Outlet context ---

export type ProcessesOutletContext = {
  pageStatuses: Record<string, ProcessStatus>;
  pageSignOffs: Record<string, SignOffs>;
  processFsaOverrides: Record<string, string[]>;
  pageCollaboration: Record<string, MockPageCollaboration>;
  /** Live status overrides for risks/controls — keyed by risk or control id */
  itemStatusOverrides: Record<string, ProcessStatus>;
  pageLocks: Record<string, PageLockState>;
  onPageStatusChange: (pageKey: string, status: ProcessStatus) => void;
  onItemStatusChange: (itemId: string, status: ProcessStatus) => void;
  onPageLockChange: (pageKey: string, locked: boolean) => void;
  onPageSignOffChange: (pageKey: string, role: ConfiguredRole, signer: string | null) => void;
  onPageSignOffStatusChange: (
    pageKey: string,
    role: ConfiguredRole,
    status: MockReviewStatus,
    comment?: string,
  ) => void;
  onAddAdditionalReviewer: (pageKey: string, signer: string) => void;
  onRemoveAdditionalReviewer: (pageKey: string, reviewerId: string) => void;
  onAdditionalReviewerStatusChange: (
    pageKey: string,
    reviewerId: string,
    status: MockReviewStatus,
    comment?: string,
  ) => void;
  onPageChatSend: (pageKey: string, message: string) => void;
  openCollaborationDrawer: (pageKey: string) => void;
  onFsaChange: (processId: string, fsaIds: string[]) => void;
  navigateTo: (sel: Selection) => void;
  openReport: (report: ReportOption) => void;
  isSimplified: boolean;
};

// --- Path helpers ---

function validateFsaSubPage(sub: string | undefined): FsaSubPage {
  if (sub === "risk-assessment" || sub === "linked-processes" || sub === "strategy") return sub;
  return "scoping";
}

function validateElcSubPage(elcId: string, sub: string | undefined): ElcSubPage | undefined {
  if (!elcHasSubPages(elcId)) return undefined;
  const validSubPages = new Set(getElcSubPages(elcId).map(s => s.key));
  if (
    (sub === "understanding" ||
      sub === "complexity" ||
      sub === "questionnaires" ||
      sub === "risks" ||
      sub === "controls" ||
      sub === "findings" ||
      sub === "matrix" ||
      sub === "summary") &&
    validSubPages.has(sub)
  )
    return sub;
  return "questionnaires";
}

function validateProcessSubPage(
  sub: string | undefined,
): "understanding" | "risks" | "controls" | "findings" | "matrix" | "questionnaires" | "summary" {
  if (
    sub === "risks" ||
    sub === "controls" ||
    sub === "findings" ||
    sub === "matrix" ||
    sub === "questionnaires" ||
    sub === "summary"
  )
    return sub;
  return "understanding";
}

function validateFraudSubPage(sub: string | undefined): FraudSubPage {
  if (
    sub === "risk-factors" ||
    sub === "team-discussion" ||
    sub === "identified-risks" ||
    sub === "management-override" ||
    sub === "strategy"
  )
    return sub;
  return "risk-factors";
}

function validateAuditProgrammeSubPage(sub: string | undefined): AuditProgrammeSubPage {
  if (sub === "by-process" || sub === "by-elc") return sub;
  return "by-fsa";
}

export function selectionFromPath(relativePath: string): Selection {
  const parts = relativePath.split("/");
  const section = parts[0];
  if (section === "materiality") return {type: "materiality"};
  if (section === "fraud") return {type: "fraud", subPage: validateFraudSubPage(parts[1])};
  if (section === "audit-programme") {
    return {type: "audit-programme", subPage: validateAuditProgrammeSubPage(parts[1])};
  }
  if (section === "financial-statement-areas") {
    if (parts[1]) return {type: "fsa", fsaId: parts[1], subPage: validateFsaSubPage(parts[2])};
  }
  if (section === "entity-level-controls") {
    if (parts[1]) return {type: "elc", elcId: parts[1], subPage: validateElcSubPage(parts[1], parts[2])};
  }
  if (section === "processes") {
    if (parts[1] === "ai-enumerator") return {type: "tool", toolId: "process-enumerator"};
    if (parts[1]) return {type: "process", processId: parts[1], subPage: validateProcessSubPage(parts[2])};
  }
  return {type: "materiality"};
}

export function selectionToPath(sel: Selection): string {
  switch (sel.type) {
    case "tool":
      if (sel.toolId === "process-enumerator") return "processes/ai-enumerator";
      return "";
    case "elc":
      return sel.subPage ? `entity-level-controls/${sel.elcId}/${sel.subPage}` : `entity-level-controls/${sel.elcId}`;
    case "fsa":
      return `financial-statement-areas/${sel.fsaId}/${sel.subPage}`;
    case "process":
      return `processes/${sel.processId}/${sel.subPage}`;
    case "materiality":
      return "materiality";
    case "fraud":
      return sel.subPage ? `fraud/${sel.subPage}` : "fraud/risk-factors";
    case "risk-register":
      return "risk-register";
    case "audit-programme":
      return `audit-programme/${sel.subPage}`;
    default:
      return "";
  }
}

// --- Functions ---

export function formatRelativeTime(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
